/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Image from '@atlaskit/image';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type ActionKeys,
	JIRA_PLANS_MANAGE_FEATURES_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { JiraPlansManageFeaturesPayload } from '@atlassian/conversation-assistant-message-actions-api/jira';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';
import { jiraPlansActionData } from '../../common/utils/plan-action-data';

import messages from './messages';

const INTERACTION_SOURCE = 'jiraPlansManageFeaturesRenderer';

const releasesFeatureIcon =
	'data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgOTYgOTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+Cgo8cGF0aCBkPSJNMjIgMzRINjEuNUw3MCA2NUgyMlYzNFoiIGZpbGw9IiM4MkI1MzYiLz4KPHBhdGggZD0iTTE1LjQxOTYgNzlMOSA1OUg0OC41TDYwLjgyOCA0OUg4Nkw3OC41OTY1IDc5SDE1LjQxOTZaIiBmaWxsPSIjNjY5REYxIi8+CjxyZWN0IHg9IjI5IiB5PSIzNCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjYiIGZpbGw9IiMxNjRCMzUiLz4KPHJlY3QgeD0iNDYiIHk9IjIzIiB3aWR0aD0iMTAiIGhlaWdodD0iMTUiIGZpbGw9IiNGQ0E3MDAiLz4KPHJlY3QgeD0iNDYiIHk9IjM0IiB3aWR0aD0iMTAiIGhlaWdodD0iNiIgZmlsbD0iIzE2NEIzNSIvPgo8cGF0aCBkPSJNMjIgNjdWNTlINDguNUw2MC41IDQ5SDY1LjYwOTRMNzAgNjdIMjJaIiBmaWxsPSIjMDkzMjZDIi8+CjxwYXRoIGQ9Ik02MiA2OUg1OUw1NSA3M0w1OSA3N0g2Mkw1OCA3M0w2MiA2OVoiIGZpbGw9IiMxRjFGMjEiLz4KPHBhdGggZD0iTTcwIDY5SDczTDc3IDczTDczIDc3SDcwTDc0IDczTDcwIDY5WiIgZmlsbD0iIzFGMUYyMSIvPgo8cGF0aCBkPSJNNjkgNjlINjYuNUw2MyA3N0g2Nkw2OSA2OVoiIGZpbGw9IiMxRjFGMjEiLz4KPHBhdGggZD0iTTI5Ljk3NTYgMTMuMTY1MkMyOS44NDggMTMuMTY1MiAyOS43MjA0IDEzLjE2NzEgMjkuNTk0NyAxMy4xNzI3QzI3LjY1MjUgNy40MTAzIDIzLjU3NDIgNSAxNy40OTYyIDVDOC43ODA4MSA1IDQgMTAuMDQ3NyA0IDIzSDM5QzM4LjAxMzcgMTYuNTU4MyAzNC42ODA5IDEzLjE2NzEgMjkuOTc3NCAxMy4xNjcxTDI5Ljk3NTYgMTMuMTY1MloiIGZpbGw9IiM0QjRENTEiLz4KPHBhdGggZD0iTS01LjE4ODIxIDIzSDEwQzguNjM4ODggMTUuNzk0NSA0LjA0MzkzIDEyIC0yLjQ0MDE2IDEyQy04LjQ3ODM2IDEyIC0xMi41NzM1IDE0Ljg4IC0xNSAyM0gtNS4xODU4NkgtNS4xODgyMVoiIGZpbGw9IiM0QjRENTEiLz4KPHBhdGggZD0iTTMuMjA0MjYgMjJIMTlDMTcuNTg0NCAyOS44NjA2IDEyLjgwNTcgMzQgNi4wNjIyMyAzNEMtMC4yMTc0OTggMzQgLTQuNDc2MzkgMzAuODU4MiAtNyAyMkgzLjIwNjdIMy4yMDQyNloiIGZpbGw9IiM0QjRENTEiLz4KPC9zdmc+Cg==';

const styles = cssMap({
	footer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: token('space.100'),
	},
	featureCard: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.large'),
	},
	featureCardWrapper: {
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
});

export const filterManageFeaturesActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansManageFeatures]>[] => {
	const filteredActions = actions.filter((action) => assertIsManageFeaturesAction(action));
	return filteredActions as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansManageFeatures]
	>[];
};

const assertIsManageFeaturesAction = (action: MessageAction): MessageAction => {
	if (!jiraPlansActionData.isPlanActionKeyForRenderer(action.key, JIRA_PLANS_MANAGE_FEATURES_KEY)) {
		throw new Error('Invalid action key for ManageFeaturesAction');
	}
	return action;
};

export const ManageFeaturesAction = (
	props: ActionRendererFor<typeof ActionKeys.JiraPlansManageFeatures>,
): JSX.Element | null => {
	const { actions } = props;

	const firstAction = useMemo(() => filterManageFeaturesActions(actions)[0], [actions]);
	if (!firstAction) {
		return null;
	}

	return <ManageFeaturesActionContent {...props} firstAction={firstAction} />;
};

const ManageFeaturesActionContent = (
	props: ActionRendererFor<typeof ActionKeys.JiraPlansManageFeatures> & {
		firstAction: ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansManageFeatures]>;
	},
) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	const {
		getStatus,
		getReadonly,
		firstAction,
		onConfirm,
		onCancel,
		actions,
		sendMessageConfirmedTools,
		recipientAgentId,
		recipientAgentNamedId,
		conversationId,
		messageId,
	} = props;

	const isCustomAgent = !isDefaultRovoChat({
		agentId: recipientAgentId,
		agentNamedId: recipientAgentNamedId,
	});

	const { data } = firstAction;
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const invocationStatus = String(status ?? '').toLowerCase();
	const actionStatus = String(firstAction.status ?? '').toLowerCase();

	const isActionLoading = invocationStatus !== 'idle';
	const isSubmitting = invocationStatus === 'started';
	const isActionFinished = invocationStatus === 'finished' || actionStatus === 'finished';
	const buttonsDisabled = isActionLoading || readonly;

	const actionData = jiraPlansActionData.getPlanActionData<JiraPlansManageFeaturesPayload>(data);

	const isEnabling = actionData.isReleaseEnabled;

	const handleConfirm = useCallback(() => {
		sendAnalyticsEvent({
			actionSubjectId: firstAction.actionId,
			action: 'clicked',
			actionSubject: 'button',
			attributes: {
				actionSubjectId: 'rovo-chat-jira-plans-manage-features-confirm',
				planId: actionData.planId,
				isReleaseEnabled: actionData.isReleaseEnabled,
			},
		});

		if (
			fg('jira_plans_rovo_tools_resumption_migration') ||
			(isCustomAgent && fg('enable_skills_ga_tools_in_studio'))
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: INTERACTION_SOURCE },
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed')
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
			return;
		}

		onConfirm(firstAction);
	}, [
		firstAction,
		actions,
		actionData,
		getStatus,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		sendAnalyticsEvent,
		onConfirm,
		isCustomAgent,
	]);

	const handleCancel = useCallback(() => {
		sendAnalyticsEvent({
			actionSubjectId: firstAction.actionId,
			action: 'clicked',
			actionSubject: 'button',
			attributes: {
				actionSubjectId: 'rovo-chat-jira-plans-manage-features-cancel',
				planId: actionData.planId,
			},
		});

		if (
			fg('jira_plans_rovo_tools_resumption_migration') ||
			(isCustomAgent && fg('enable_skills_ga_tools_in_studio'))
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: INTERACTION_SOURCE },
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) => buildToolFromAction(action, 'cancelled')),
				},
			});
			return;
		}

		onCancel(firstAction);
	}, [
		firstAction,
		actions,
		actionData,
		getStatus,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		sendAnalyticsEvent,
		onCancel,
		isCustomAgent,
	]);

	const featureCard = useMemo(
		() => (
			<Box xcss={styles.featureCardWrapper}>
				<Box xcss={styles.featureCard}>
					<Inline space="space.100" alignBlock="center">
						<Image src={releasesFeatureIcon} alt="" height="24px" width="24px" />
						<Text>{formatMessage(messages.releasesLabel)}</Text>
					</Inline>
					<Lozenge appearance={isEnabling ? 'success' : 'danger'}>
						{isEnabling ? formatMessage(messages.statusOn) : formatMessage(messages.statusOff)}
					</Lozenge>
				</Box>
			</Box>
		),
		[isEnabling, formatMessage],
	);

	if (!actionData.planId || typeof actionData.isReleaseEnabled !== 'boolean') {
		return null;
	}

	return (
		<ActionCardWrapper isExpandable title={formatMessage(messages.cardTitle)} product="plan">
			{featureCard}

			{!isActionFinished && (
				<ActionCardFooter>
					<Box xcss={styles.footer}>
						<Button appearance="default" onClick={handleCancel} isDisabled={buttonsDisabled}>
							{formatMessage(messages.cancelButton)}
						</Button>
						<Button
							appearance={isEnabling ? 'primary' : 'danger'}
							onClick={handleConfirm}
							isDisabled={buttonsDisabled}
							isLoading={isSubmitting}
						>
							{isEnabling
								? formatMessage(messages.turnOnButton)
								: formatMessage(messages.turnOffButton)}
						</Button>
					</Box>
				</ActionCardFooter>
			)}
		</ActionCardWrapper>
	);
};
